import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { i18N } from '@/common/util'

import useSystemStore from './systemStore'
import useAuthStore from './authStore'

import sectionService from '@/services/secao.service'
import { midiaService } from '@/services/midias.service'
import oldMediaService from '@/services/midias.service'
import { courseService } from '@/services/cursos.service'
import { formationService } from '@/services/formacao.service'
import { useJourneys } from '@/composables/useJourneys'

import type { LoaderType, LsSection, LearningSection, LearningCard } from '@/stores/storeTypes'

type ItemType = 'media' | 'course' | 'trail'

export const useLearningStore = defineStore('learning', () => {
  const sys = useSystemStore()
  const auth = useAuthStore()
  const journeys = useJourneys()

  // LsSection['items']
  const specialSections = ref<any>([
    {
      id: 'continue',
      name: i18N('mainContent.sections.keepLearning'),
      observation: sys.getConfig.general.hideDescriptionSection ? '' : i18N('mainContent.sections.keepLearningDescription'),
      type: 'content',
      backgroundImage: '',
      page: 0,
      totalItems: 0,
      items: []
    },
    {
      id: 'suggested',
      name: i18N('mainContent.sections.suggestedForYou'),
      observation: sys.getConfig.general.hideDescriptionSection ? '' : i18N('mainContent.sections.suggestedForYouDescription'),
      type: 'content',
      backgroundImage: '',
      page: 0,
      totalItems: 0,
      items: []
    },
    {
      id: 'seeAgain',
      name: i18N('mainContent.sections.seeAgain'),
      observation: sys.getConfig.general.hideDescriptionSection ? '' : i18N('mainContent.sections.seeAgainDescription'),
      type: 'content',
      backgroundImage: '',
      page: 0,
      totalItems: 0,
      items: []
    },
    {
      id: 'favorites',
      name: i18N('button.myFavorites'),
      observation: '',
      type: 'content',
      backgroundImage: '',
      page: 0,
      totalItems: 0,
      items: []
    }
  ])

  const storageSessID = ref('')
  const homeSections = ref<LearningSection>({})
  const catalogSections = ref<LearningSection>({})
  const cards = ref<LearningCard>([])
  const firstNormalSection = ref<any>(null)

  // Cache dos itens das seções (por id) para reexibir os cards atuais enquanto a home recarrega (stale-while-revalidate)
  const homeSectionsItemsCache = ref<Record<string, any[]>>({})

  const clearStorage = () => {
    homeSections.value = { pagination: { current: 0, size: 1, total: 0, loaded: [] } }
    catalogSections.value = { pagination: { current: 0, size: 1, total: 0, loaded: [] } }
    cards.value = []
    lastViewed.value = []
    firstNormalSection.value = null
    homeSectionsItemsCache.value = {}
  }

  // Armazena os ultimos 10 items visualizados
  const lastViewed = ref<{
    type: ItemType
    id: string
    data: any
  }[]>([])

  const viewing = ref<{
    type: ItemType
    id: string
    data: any
    parentSection?: string
    trackProgress?: boolean
    // Preenchido quando o item foi aberto a partir da tela da jornada
    journeyId?: string
  } | null>(null)

  const activeRequests = ref({
    sections: 0
  })
  const onGoingRequests = { catalog: [] as AbortController[] }
  const status = computed(() => ({
    sections: activeRequests.value.sections > 0 ? 'loading' : 'idle'
  }))

  const getSections = async (obj?: { page?: number, size?: number, target?: 'home' | 'catalog', filters?: Record<string, any> }, cleanup = false) => {
    if (onGoingRequests.catalog.length > 0) {
      // Aborta todas as requisições em andamento
      onGoingRequests.catalog.forEach(abC => abC.abort())
      onGoingRequests.catalog.splice(0, onGoingRequests.catalog.length)
    }

    const ukv = `${auth.userId}-${auth.getActiveTentant}`
    if (ukv !== storageSessID.value) {
      clearStorage()
      storageSessID.value = ukv
    }

    const page = obj?.page || 1
    const size = obj?.size || 6
    const target = obj?.target || 'home'

    const targetSections = target === 'home' ? homeSections : catalogSections

    if (cleanup) {
      // Limpa as seções antes de buscar novas
      targetSections.value = { pagination: { current: page, size, total: 0, loaded: [] } }
    }

    if (!targetSections.value[page]) {
      targetSections.value[page] = {
        page,
        status: 'loading',
        lastUpdated: Date.now(),
        items: []
      }
    } else {
      targetSections.value[page].status = 'updating'
    }

    // Lógica para Home: Página 1 são as seções especiais + possível 1ª seção normal (se for jornadas)
    if (target === 'home' && page === 1) {
      return loadFirstPageHome(target, targetSections, size)
    }

    const params : Record<string, any> = {
      page,
      size
    }

    let currentFilters = ''
    if (obj?.filters && typeof obj.filters === 'object') {
      // Adiciona os filtros ao params
      Object.keys(obj.filters).forEach((key) => {
        if (Array.isArray(obj.filters?.[key]) && obj.filters[key].length > 0) {
          params[key] = obj.filters[key]
        } else if (typeof obj.filters?.[key] === 'string' && obj.filters[key].trim() !== '') {
          params[key] = obj.filters[key]
        }
      })

      currentFilters = JSON.stringify(obj.filters)
    }

    if (target === 'home') {
      params.inLandingPage = true
      if (page > 1 && !firstNormalSection.value) params.page = page - 1

    } else {
      // @ATTENTION GBT para ignorar top10 e lancamentos no catalogo
      if (!params.types || Array.isArray(params.types) && params.types.length === 0) {
        params.types = ['conteudo', 'comunicacao']
      } else {
        params.types = ['conteudo']
      }
    }

    const abC = new AbortController()
    onGoingRequests.catalog.push(abC)

    activeRequests.value.sections += 1
    return sectionService.findActive(params, { signal: abC.signal })
      .then((response) => {
        const { success, data, totalPages } = response || {}

        if (success) {
          if (targetSections.value.filters !== currentFilters) {
            targetSections.value[page].items = []
            targetSections.value.filters = currentFilters
          }

          // Ajusta o total de páginas para Home
          // Se firstNormalSection já consumiu a page 1 da API, não precisa do +1
          const adjustedTotal = target === 'home'
            ? (firstNormalSection.value ? totalPages : (Number(totalPages) + 1))
            : totalPages

          if (targetSections.value.pagination) {
            targetSections.value.pagination.total = adjustedTotal
            targetSections.value.pagination.current = page

            // Caso a página não tenha sido carregada anteriormente
            if (!targetSections.value.pagination.loaded?.includes(page)) {
              targetSections.value.pagination.loaded?.push(page)
            }
          }

          targetSections.value[page].status = 'success'
          targetSections.value[page].lastUpdated = Date.now()

          // Atualiza os itens da seção sem remover os cards vinculados a seção
          // TODO: implementar lógica de atualização via tempo (ex: A cada 1 hora o proximo refresh limpa os cards tbm)

          // Filtra a 1ª seção normal se ela já foi exibida na página 1 (caso Jornadas)
          const filteredData = (target === 'home' && firstNormalSection.value)
            ? data.filter((section) => section.id !== firstNormalSection.value.id)
            : data

          filteredData.forEach((section) => {
            const item = targetSections.value[page].items.find((s) => s.id === section.id)
            if (item) {
              const cardsBkp = JSON.parse(JSON.stringify(item.items))

              Object.assign(item, {
                ...section,
                lastUpdated: Date.now(),
                page,
                totalItems: section.totalItems ?? 0,
                items: cardsBkp
              })
            } else {
              targetSections.value[page].items.push({
                ...section,
                lastUpdated: Date.now(),
                page,
                totalItems: section.totalItems ?? 0,
                // Reusa os itens em cache (se houver) para manter os cards na tela enquanto recarrega
                items: (target === 'home' ? homeSectionsItemsCache.value[section.id] : null) || []
              } as any)
            }
          })

          // Se seções top10/releases já possuem itens carregados, seta featuredItems a partir deles
          // (sem chamada extra de API — apenas reusa dados já existentes na listagem)
          const specialTypes = ['top10', 'releases']
          for (const sec of targetSections.value[page].items) {
            if (specialTypes.includes(sec.type)) {
              const fType = sec.type === 'top10' ? 'top10' as const : 'releases' as const
              if (sec.items?.length > 0) {
                setFeaturedItems(fType, sec.items)
              }
            }
          }

          return targetSections.value[page].items
        }
      })
      .catch((error) => {
        console.error('Error fetching sections:', error)
      })
      .finally(() => {
        // Remove a requisição da lista de requisições em andamento
        const index = onGoingRequests.catalog.indexOf(abC)
        if (index > -1) {
          onGoingRequests.catalog.splice(index, 1)
        }

        activeRequests.value.sections -= 1
      })
  }

  const getSection = (sectionId: string) => {
    return sectionService.findActive({ secoesIds: [sectionId], size: 1 })
      .then((response) => {
        const { success, data } = response || {}

        // Atualiza a seção
        if (success && data.length > 0) {
          const section = data[0]

          if (section) {
            // Função auxiliar para atualizar seções
            const updateSection = (sections: typeof homeSections.value) => {
              Object.values(sections).forEach(sec => {
                if (sec && typeof sec === 'object' && 'items' in sec && Array.isArray(sec.items)) {
                  const existingItem = sec.items.find(item => item.id === sectionId)
                  if (existingItem) {
                    Object.assign(existingItem, {
                      name: section?.name,
                      observation: section?.observation,
                      startDate: section?.startDate,
                      endDate: section?.endDate
                    })
                  }
                }
              })
            }

            updateSection(homeSections.value)
            updateSection(catalogSections.value)

            return section
          }
        }

        return null
      })
      .catch((error) => {
        console.error('Error fetching section:', error)
      })
  }

  const loadFirstPageHome = async (target: 'home' | 'catalog', targetSections: any, size: number): Promise<any[]> => {
    const specialItems = specialSections.value.filter((s: any) => s.totalItems > -1 && s.id !== 'favorites' && s.id !== 'seeAgain')
    let firstSectionTotalPages = 0

    const resetToSpecialOnly = () => {
      firstNormalSection.value = null
      targetSections.value[1].items = specialItems
    }

    try {
      const { success, data, totalPages } = await sectionService.findActive(
        { page: 1, size, inLandingPage: true }
      ) ?? {}

      const firstSection = success && data?.[0]

      if (firstSection) {
        firstNormalSection.value = firstSection
        firstSectionTotalPages = totalPages || 0

        const sectionEntries = (data as any[]).map(s => ({ ...s, lastUpdated: Date.now(), page: 1, totalItems: s.totalItems, items: homeSectionsItemsCache.value[s.id] || [] }))

        targetSections.value[1].items = firstSection.type === 'journeys'
          ? [sectionEntries[0], ...specialItems, ...sectionEntries.slice(1)]
          : [...specialItems, ...sectionEntries]
      } else {
        resetToSpecialOnly()
      }
    } catch (e) {
      console.error('Erro ao buscar 1ª seção normal:', e)
      resetToSpecialOnly()
    }

    targetSections.value[1].status = 'success'
    targetSections.value.pagination.current = 1

    if (targetSections.value.pagination.total === 0) {
      targetSections.value.pagination.total =
        firstNormalSection.value && firstSectionTotalPages > 0 ? firstSectionTotalPages : 2
    }

    if (!targetSections.value.pagination.loaded.includes(1)) {
      targetSections.value.pagination.loaded.push(1)
    }

    specialItems.forEach(section => {
      if (!section.loadedItemsPages?.includes(1)) {
        sectionItemsStatus.value[section.id] = section.items.length > 0 ? 'updating' : 'loading'
      }
    })

    loadSectionsItemsSequentially(specialItems, target)

    return targetSections.value[1].items
  }

  // Função compartilhada para carregar itens de seções sequencialmente
  const loadSectionsItemsSequentially = async (
    sections: any[],
    target: 'home' | 'catalog',
    opt = { page: 1, size: 7 }
  ) => {
    for (const section of sections) {
      if (!section.loadedItemsPages?.includes(opt.page)) {
        try {
          await getSectionItems(section.id, target, opt)
        } catch (e) {
          console.error(`Erro ao carregar seção ${section.id}:`, e)
        }
      }
    }
  }

  const sectionItemsStatus = ref<Record<string, LoaderType>>({})

  const searchData = ref({
    status: 'initial' as LoaderType,
    lastUpdated: null as number | null,
    items: {} as Record<string, { id: string, type: ItemType }[]>,
    results: {
      items: 0,
      nPages: 0
    },
    options: { page: 1, size: 16 }
  })

  const searchItems = async (opt = { page: 1, size: 16 }) => {
    searchData.value.status = 'loading'

    return sectionService.findSectionItems('00000000-0000-0000-0000-000000000000', opt)
      .then((res) => {
        const { success, data, items, nPages } = res

        if (opt.page === 1) {
          searchData.value.items = {}
        }

        if (typeof searchData.value.items[opt.page] === 'undefined') {
          searchData.value.items[opt.page] = []
        }

        data.forEach((card: any) => {
          searchData.value.items[opt.page].push({
            id: card.id,
            type: card.type
          })

          const existingCard = cards.value.findIndex(c => c.id === card.id && c.type === card.type)
          if (existingCard !== -1) {
            cards.value[existingCard].data = {
              ...cards.value[existingCard].data,
              ...card
            }
          } else {
            cards.value.push({
              id: card.id,
              type: card.type,
              data: card
            })
          }
        })

        searchData.value.status = success ? 'success' : 'error'
        searchData.value.results.items = Number(items)
        searchData.value.results.nPages = Number(nPages)
        searchData.value.lastUpdated = Date.now()
      })
      .catch((error) => {
        console.error('Error searching items:', error)
      })
  }

  const getSectionItems = async (
    id: string,
    target: 'home' | 'catalog' = 'home',
    opt = { page: 1, size: 7 },
    raw = false
  ) => {
    if (!raw) sectionItemsStatus.value[id] = 'loading'

    return sectionService.findSectionItems(id, opt)
      .then((response) => {
        const { success, data, items: total } = response

        // Atualiza os cards com os itens encontrados
        data.forEach((card: LearningCard[0]['data']) => {
          const existingCard = cards.value.findIndex(c => c.id === card.id && c.type === card.type)
          if (existingCard !== -1) {
            cards.value[existingCard].data = {
              ...cards.value[existingCard].data,
              ...card
            }
          } else {
            cards.value.push({
              id: card.id,
              type: card.type,
              data: card
            })
          }
        })

        if (raw) {
          return {
            success,
            data,
            total
          }
        }

        const SSec = specialSections.value.map((s: any) => s.id)
        if (SSec.includes(id)) {
          const section = (specialSections.value as any)?.find(s => s.id === id)

          if (!section) {
            console.error(`Seção com ID ${id} não encontrada em seções especiais`)
            sectionItemsStatus.value[id] = 'error'
            return
          }

          section.totalItems = Number(total) > 0 ? Number(total) : -1

          // Stale-while-revalidate: na 1ª página monta a lista nova e troca de uma vez,
          // mantendo os cards atuais visíveis até os novos dados chegarem (sem esvaziar a tela)
          if (opt?.page === 1 || !opt?.page) {
            const nextItems: any[] = []
            data.forEach((i: any) => {
              if (nextItems.findIndex((it: any) => it.id === i.id && it.type === i.type) === -1) {
                nextItems.push({ id: i.id, type: i.type })
              }
            })
            section.items = nextItems
            section.loadedItemsPages = [opt.page]
          } else {
            data.forEach((i: any) => {
              if (section.items.findIndex((it: any) => it.id === i.id && it.type === i.type) === -1) {
                section.items.push({ id: i.id, type: i.type })
              }
            })

            if (typeof section.loadedItemsPages === 'undefined') {
              section.loadedItemsPages = []
            }
            if (!section.loadedItemsPages.includes(opt.page)) {
              section.loadedItemsPages.push(opt.page)
            }
          }
        } else {
          // Seções padrão (Home e Catalogo)
          const targetSections = target === 'home' ? homeSections : catalogSections

          // Itera as páginas (de seções)
          for (let p = 1; p <= Number(targetSections.value?.pagination?.total); p++) {
            const section = targetSections.value[p]
            if (!section) continue

            const totalItems = section.items.length
            let objectiveMeet = false

            // Itera os itens da seção
            for (let i = 0; i < totalItems; i++) {
              const item = section.items[i]

              if (item.id === id) {
                // Stale-while-revalidate: na 1ª página monta a lista nova e troca de uma vez,
                // mantendo os cards atuais visíveis até os novos dados chegarem (sem esvaziar a tela)
                if (opt?.page === 1 || !opt?.page) {
                  const nextItems: any[] = []
                  data.forEach((i: any) => {
                    if (nextItems.findIndex((it: any) => it.id === i.id && it.type === i.type) === -1) {
                      nextItems.push({ id: i.id, type: i.type })
                    }
                  })
                  item.items = nextItems
                  item.loadedItemsPages = [opt.page]
                } else {
                  data.forEach((i: any) => {
                    if (item.items.findIndex((it: any) => it.id === i.id && it.type === i.type) === -1) {
                      item.items.push({ id: i.id, type: i.type })
                    }
                  })

                  if (typeof item.loadedItemsPages === 'undefined') {
                    item.loadedItemsPages = []
                  }
                  if (!item.loadedItemsPages.includes(opt.page)) {
                    item.loadedItemsPages.push(opt.page)
                  }
                }

                // Atualiza o total de itens da seção com o total retornado na busca dos itens
                if (total !== undefined && total !== null) {
                  item.totalItems = total
                }

                objectiveMeet = true
                break
              }
            }

            // Se o objetivo foi encontrado, quebra o loop pra otimizar o desempenho
            if (objectiveMeet) {
              break
            }
          }
        }

        sectionItemsStatus.value[id] = 'success'

        if (!success) {
          console.error('Failed to fetch section items:', data)
          sectionItemsStatus.value[id] = 'error'
          return
        }
      })
      .catch((error) => {
        console.error('Error fetching section items:', error)
        sectionItemsStatus.value[id] = 'error'
      })
  }

  // Seções acessíveis para o usuário
  const availableHomeSections = computed(() => {
    const res : LsSection['items'] = []

    Object.values(homeSections.value).forEach(section => {
      if (typeof section === 'object' && 'items' in section) {
        section.items.forEach(item => {
          if ((item as any).totalItems !== -1) {
            // Se a seção for do tipo jornada e estiver vazia, não exibe
            if (item.type === 'journeys' && journeys.status.value === 'success' && journeys.list.value.length === 0) {
              return
            }

            res.push(item as any)
          }
        })
      }
    })

    return res
  })

  const availableCatalogSections = computed(() => {
    const res : LsSection['items'] = []

    Object.values(catalogSections.value).forEach(section => {
      if (typeof section === 'object' && 'items' in section) {
        section.items.forEach(item => {
          if ((item as any).totalItems !== -1) {
            // Se a seção for do tipo jornada e estiver vazia, não exibe
            if (item.type === 'journeys' && journeys.status.value === 'success' && journeys.list.value.length === 0) {
              return
            }

            res.push(item as any)
          }
        })
      }
    })

    return res
  })

  const availableCards = computed(() => {
    return {
      media: cards.value.filter(c => c.type === 'media')?.map(c => c.data),
      course: cards.value.filter(c => c.type === 'course')?.map(c => c.data),
      trail: cards.value.filter(c => c.type === 'trail')?.map(c => c.data)
    }
  })

  const toggleFavorite = (cardId: string, type: ItemType) => {
    const cardItem = cards.value.find(c => c.id === cardId && c.type === type)?.data

    if (cardItem) {
      cardItem.favorite = !cardItem.favorite

      return type === 'media' ? midiaService.bookmark(cardId, !cardItem.favorite)
        : type === 'course' ? courseService.bookmark(cardId, !cardItem.favorite)
        : formationService.bookmark(cardId, !cardItem.favorite)
    }

    return Promise.reject(new Error('Card item not found for toggling favorite.'))
  }

  const viewItemStatus = ref<'initial' | 'loading' | 'reloading' | 'cached' | 'error' | 'success'>('initial')

  const viewItem = async (id: string, type: ItemType, section = '', skipTrack = false, journeyId?: string) => {

    // Verifica se o item já foi visualizado
    const existingIndex = lastViewed.value.findIndex(item => item.id === id && item.type === type)

    if (existingIndex !== -1) {
      viewing.value = lastViewed.value[existingIndex]
      if (section) {
        viewing.value.parentSection = section
      }
      if (skipTrack) {
        viewing.value.trackProgress = false
      }
      // Sempre sobrescreve para não herdar a jornada de uma visualização anterior do mesmo item
      viewing.value.journeyId = journeyId

      viewItemStatus.value = 'cached'
      return viewing.value
    }

    const pushItem = ({ data }: any) => {
      viewItemStatus.value = 'success'
      if (!data) return

      const newItem = { type, id, data, parentSection: section, trackProgress: !skipTrack, journeyId }

      lastViewed.value.unshift(newItem)
      if (lastViewed.value.length > 10) {
        lastViewed.value.pop()
      }

      viewing.value = newItem
      if (section) {
        viewing.value.parentSection = section
      }
    }

    viewItemStatus.value = 'loading'

    try {
      const response = await sectionService.itemDetail({ type, id })

      pushItem(response)

      return viewing.value
    } catch (error) {
      viewItemStatus.value = 'error'
      throw new Error('Erro ao carregar o item solicitado.')
    }
  }

  const interruptItem = () => {
    if (viewing.value) {
      viewing.value = null
    }
  }

  const activeItem = computed(() => {
    return viewing.value
  })

  const activeItemRelated = computed(() => {
    if (!viewing.value || !viewing.value.parentSection) return []

    let targetSection = availableCatalogSections.value.find(section => section.id === viewing.value?.parentSection)
    if (!targetSection) {
      targetSection = availableHomeSections.value.find(section => section.id === viewing.value?.parentSection)
      if (!targetSection) return []
    }

    return targetSection.items.filter(item => item.id !== viewing.value?.id)
  })

  const setEnrollment = async (id: string, type: 'course' | 'trail', enroll: boolean, journeyId?: string) => {
    const { success } = (
      (type.toLowerCase()) === 'course'
        ? await (enroll ? courseService.enroll(id, journeyId) : courseService.unenroll(id))
        : await (enroll ? formationService.enroll(id,journeyId) : formationService.unenroll(id))
    )
    if (success) {
      // Atualiza o card correspondente
      const cardItem = cards.value.find(c => c.id === id && c.type === type)?.data
      if (cardItem) {
        cardItem.enrolledAt = enroll ? new Date() : null
      }

      // Atualiza o status do item visualizado
      if (viewing.value && viewing.value.id === id && viewing.value.type === type) {
        viewing.value.data.dataInscricao = enroll ? new Date() : null
      }
    }
  }

  const updateCardProgress = (id: string, type: 'course' | 'trail' | 'media', progress: number, journeyId?: string) => {
    return oldMediaService.registerProgress(id, progress, {
      isClassroom: false,
      inscriptionId: null,
      journeyId
    }).then(({ success }) => {
      if (success) {
        const card = availableCards.value[type].find(c => c.id === id)
        if (card && type === 'media' && card.progress < progress) {
          card.progress = progress
        }
      }
    })
  }

  const quickEdit = async (sectionId: string, data: { id?: string, name?: string, observation?: string, showObservation?: boolean }) => {
    const { data: secao } = await sectionService.get<any>(sectionId)

    if (data && data?.id && secao) {
      secao.nome = data.name
      secao.observacao = data.observation
      secao.exibirDescricao = data.showObservation

      const { data: result } = await sectionService.put(data.id, secao)

      if (result?.id) {
        // Atualiza a seção na home localmente
        const homePages = Number(homeSections.value?.pagination?.total)
        for (let p = 1; p <= homePages; p++) {
          const page = homeSections.value[p]
          if (page && page.items && Array.isArray(page.items)) {
            const item = page.items.find(s => s.id === sectionId)
            if (item) {
              item.name = result.nome
              item.observation = result.observacao
              item.showObservation = result.exibirDescricao
            }
          }
        }

        // Atualiza a seção no catálogo localmente
        const catalogPages = Number(catalogSections.value?.pagination?.total)
        for (let p = 1; p <= catalogPages; p++) {
          const page = catalogSections.value[p]
          if (page && page.items && Array.isArray(page.items)) {
            const item = page.items.find(s => s.id === sectionId)
            if (item) {
              item.name = result.nome
              item.observation = result.observacao
              item.showObservation = result.exibirDescricao
            }
          }
        }
      }
    }
  }

  /**
   * Mecanismo para obter os 10 itens mais populares da plataforma (Para identificar itens de top10 em outras seções)
   */

  const featuredItems = ref<{
    top10: { id: string, type: ItemType, pos: number }[],
    releases: { id: string, type: ItemType, pos: number }[]
  }>({
    top10: [],
    releases: []
  })

  let ttlFeaturedItems = 0

  const setFeaturedItems = (type: 'top10' | 'releases', items: any[]) => {
    if (!items || items.length === 0) return

    featuredItems.value[type] = items.map((d: any, index: number) => ({ id: d.id, type: d.type, pos: index + 1 }))
    ttlFeaturedItems = Date.now()
  }

  const getFeaturedItems = async () => {
    // Os featuredItems agora são extraídos automaticamente pelo getSections()
    // quando seções do tipo top10/releases são retornadas na listagem normal.
    // Este código abaixo está comentado temporariamente.
    return

    // // Se já temos os dados e não expirou o cache (5 minutos), retorna os dados em cache
    // if ((featuredItems.value.top10.length > 0 || featuredItems.value.releases.length > 0) && (Date.now() - ttlFeaturedItems) < 300000) {
    //   return
    // }
    //
    // return sectionService.findActive({
    //   types: ['top10', 'lancamentos'],
    //   page: 1,
    //   size: 2
    // })
    // .then(async ({ success, data }) => {
    //   if (!success || !data.length)
    //     return
    //
    //   const reqs = []
    //   for (const section of data) {
    //     let type = section.type === 'top10' ? 'top10' as const : 'releases' as const
    //
    //     reqs.push(
    //       getSectionItems(section.id, 'catalog', { page: 1, size: type === 'top10' ? 10 : 16 }, true)
    //         .then((res) => {
    //           const { success: s2, data: d2 } = res || {}
    //
    //           if (s2)
    //             setFeaturedItems(type, d2)
    //         })
    //     )
    //   }
    //
    //   await Promise.all(reqs)
    // })
    // .catch((error) => {
    //   console.error('Error fetching featured items:', error)
    // })
  }

  /**
   * Verifica se um item está entre os 10 mais populares
   * @param id
   * @param type
   * @returns Posição do item no top10 ou 0 se não estiver no top10
   */
  const isTop10 = (id: string, type: ItemType) => {
    const idx = featuredItems.value.top10.findIndex(item => item.id === id && item.type === type)

    return idx !== -1 ? featuredItems.value.top10[idx].pos : 0
  }

  const isReleases = (id: string, type: ItemType) => {
    return featuredItems.value.releases.findIndex(item => item.id === id && item.type === type) !== -1
  }

  const closeItem = () => {
    viewing.value = null
    viewItemStatus.value = 'initial'
  }

  // const resetStore = () => {
  //   clearStorage()
  //   specialSections.value = []
  //   storageSessID.value = ''
  //   lastViewed.value = []
  //   viewing.value = null
  //   viewItemStatus.value = 'initial'
  //   sectionItemsStatus.value = {}
  //   featuredItems.value = { top10: [], releases: [] }
  //   ttlFeaturedItems = 0
  // }

  const loadSeeAgain = () => {
    const seeAgainSection = specialSections.value.find((s: any) => s.id === 'seeAgain')
    if (!seeAgainSection) return Promise.resolve()

    seeAgainSection.totalItems = 0
    seeAgainSection.items = []
    seeAgainSection.loadedItemsPages = []

    const lastPage = homeSections.value.pagination?.current || 1
    const pageData = homeSections.value[lastPage]
    if (pageData && Array.isArray(pageData.items) && !pageData.items.find((s: any) => s.id === 'seeAgain')) {
      pageData.items.push(seeAgainSection)
    }

    return getSectionItems('seeAgain', 'home')
  }

  const refreshSections = () => {
    // Guarda os itens atuais das seções (por id) antes de zerar, para reexibir os cards enquanto recarrega (stale-while-revalidate)
    const cache: Record<string, any[]> = {}
    Object.values(homeSections.value).forEach((page: any) => {
      if (page && Array.isArray(page.items)) {
        page.items.forEach((sec: any) => {
          if (sec?.id && Array.isArray(sec.items) && sec.items.length) {
            cache[sec.id] = sec.items
          }
        })
      }
    })
    homeSectionsItemsCache.value = cache

    homeSections.value = { pagination: { current: 0, size: 1, total: 0, loaded: [] } }
    firstNormalSection.value = null
    specialSections.value.forEach((s: any) => {
      s.totalItems = 0
      s.page = 0
      s.loadedItemsPages = []
      // Mantém s.items para exibir os cards atuais enquanto a nova busca não conclui (stale-while-revalidate)
    })
  }

  interface ReactToViewingItemParams {
    id: string
    totalReacoes: number
    reacao: string
  }

  const reactToViewingItem = (result: ReactToViewingItemParams) => {
    if (!result || !viewing.value) return

    const card = cards.value.find(c => c.id === viewing.value?.id && c.type === viewing.value?.type)
    if (card) {
      card.data.totalReactions = result.totalReacoes
      card.data.reaction = result.reacao

      if (activeItem.value) {
        activeItem.value.data.totalReactions = result.totalReacoes
        activeItem.value.data.reaction = result.reacao
      }
    }
  }

  return {
    status,
    homeSections: availableHomeSections,
    specialSections,
    catalogSections: availableCatalogSections,
    catalogPagination: computed(() => catalogSections.value.pagination),
    homePagination: computed(() => homeSections.value.pagination),
    cards: availableCards,
    activeItem,
    activeItemRelated,
    lastViewed,
    viewItemStatus,
    getSection,
    getSections,
    sectionItemsStatus,
    searchData,
    searchItems,
    getSectionItems,
    setEnrollment,
    toggleFavorite,
    viewItem,
    closeItem,
    interruptItem,
    clearStorage,
    updateCardProgress,
    getFeaturedItems,
    isTop10,
    isReleases,
    quickEdit,
    refreshSections,
    loadSeeAgain,
    reactToViewingItem
  }
})